import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Link2, Users, LayoutDashboard, PlayCircle, Mail, MousePointerClick, UserPlus, ShieldCheck, KeyRound, CheckCircle2, FileBadge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/me", label: "Dashboard", icon: LayoutDashboard, testId: "nav-me-dashboard" },
  { to: "/me/students", label: "My Students", icon: Users, testId: "nav-me-students" },
  { to: "/me/link", label: "My Link", icon: Link2, testId: "nav-me-link" },
  { to: "/me/inbox", label: "Inbox", icon: Mail, testId: "nav-me-inbox" },
  { to: "/me/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-me-sample-report" },
  { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-me-demo" },
];

const FUNNEL_STEPS = [
  { key: "link_clicks", label: "Link Clicks", icon: MousePointerClick },
  { key: "students_registered", label: "Registered", icon: UserPlus },
  { key: "students_verified", label: "Verified", icon: ShieldCheck },
  { key: "licenses_activated", label: "Activated", icon: KeyRound },
  { key: "assessments_started", label: "Started", icon: PlayCircle },
  { key: "assessments_submitted", label: "Completed", icon: CheckCircle2 },
  { key: "reports_generated", label: "Reports", icon: FileBadge },
];

export default function MarketingDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [exec, setExec] = useState(null);

  useEffect(() => {
    api.get("/marketing/dashboard").then((res) => setMetrics(res.data));
    api.get("/marketing/me").then((res) => setExec(res.data));
  }, []);

  const copyLink = () => {
    if (!exec) return;
    navigator.clipboard.writeText(`${window.location.origin}/start?ref=${exec.referral_code}`);
    toast.success("Referral link copied");
  };

  return (
    <DashboardShell links={LINKS} activePath="/me">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1" data-testid="marketing-welcome-heading">
        Welcome, {exec?.name || "Executive"}
      </h1>
      <p className="text-sm text-slate-500 mb-8">Track your CareerStudy360 referral performance.</p>

      {metrics && (
        <>
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5 mb-6 flex items-center justify-between" data-testid="license-pool-card">
            <div>
              <p className="text-xs text-indigo-500 font-medium mb-1">Licenses Remaining in Your Pool</p>
              <p className="text-3xl font-bold font-mono text-indigo-700">{metrics.license_pool ?? 0}</p>
            </div>
            <p className="text-xs text-indigo-500 max-w-xs text-right">
              Each new student registration under your referral code consumes one license. Contact CareerStudy360 admin to top up.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
            {FUNNEL_STEPS.map((s) => (
              <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`metric-tile-${s.key}`}>
                <s.icon className="h-4 w-4 text-indigo-500 mb-2" />
                <p className="text-2xl font-bold text-slate-900 font-mono">{metrics[s.key] ?? 0}</p>
                <p className="text-xs text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-6 mb-10">
            <h3 className="font-heading font-semibold text-slate-900 mb-5">Conversion Funnel</h3>
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {FUNNEL_STEPS.map((s, i) => (
                <div key={s.key} className="flex items-center gap-2 shrink-0">
                  <div className="text-center px-4">
                    <p className="text-lg font-bold text-indigo-600 font-mono">{metrics[s.key] ?? 0}</p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                  {i < FUNNEL_STEPS.length - 1 && <span className="text-slate-300">→</span>}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading font-semibold text-slate-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="quick-action-launch-demo" onClick={() => navigate("/me/demo")} className="rounded-full bg-indigo-600 hover:bg-indigo-700">Launch Demo</Button>
          <Button data-testid="quick-action-copy-link" variant="outline" onClick={copyLink}>Copy Referral Link</Button>
          <Button data-testid="quick-action-view-students" variant="outline" onClick={() => navigate("/me/students")}>View Students</Button>
          <Button data-testid="quick-action-qr-code" variant="outline" onClick={() => navigate("/me/link")}>Generate QR Code</Button>
        </div>
      </div>
    </DashboardShell>
  );
}
